import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MessageSquare, Sparkles, Send } from "lucide-react";
import { toast } from "sonner";
import { SectionHeader } from "@/components/meeting/SectionHeader";

const SUGGESTIONS = [
  "Quelles décisions ont été prises sur le budget ?",
  "Qui est responsable du suivi client ?",
  "Génère un email de suivi pour les participants",
];

export const ChatTab = ({ meetingId }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    api
      .get(`/meetings/${meetingId}/chat`)
      .then(({ data }) => setMessages(data || []))
      .catch((err) => console.error("Failed to load chat history:", err));
  }, [meetingId]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = async (e) => {
    e?.preventDefault();
    if (!input.trim() || sending) return;
    const msg = input.trim();
    setInput("");
    setSending(true);
    setMessages((m) => [...m, {
      id: `tmp-${Date.now()}`, role: "user", content: msg, created_at: new Date().toISOString(),
    }]);
    try {
      const { data } = await api.post(`/meetings/${meetingId}/chat`, { message: msg });
      setMessages((m) => [...m.filter((x) => !x.id.startsWith("tmp-")), data.user, data.assistant]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur assistant");
      setMessages((m) => m.filter((x) => !x.id.startsWith("tmp-")));
    } finally {
      setSending(false);
    }
  };

  return (
    <Card className="bg-af-card border-af rounded-md flex flex-col h-[600px]" data-testid="chat-card">
      <div className="p-6 border-b border-af">
        <SectionHeader icon={MessageSquare} title="Assistant IA" subtitle="Posez des questions sur cette réunion" />
      </div>
      <div className="flex-1 overflow-y-auto p-6 space-y-4" data-testid="chat-messages">
        {messages.length === 0 && (
          <div className="text-center py-12">
            <Sparkles className="h-8 w-8 mx-auto text-terracotta mb-3" />
            <p className="text-af-secondary text-sm mb-4">Exemples de questions :</p>
            <div className="flex flex-wrap gap-2 justify-center">
              {SUGGESTIONS.map((q, i) => (
                <button key={q} onClick={() => setInput(q)}
                  data-testid={`suggestion-${i}`}
                  className="px-3 py-1.5 text-xs bg-af-subtle border border-af rounded-md text-af-secondary hover:bg-white hover:text-af transition-all">
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m) => (
          <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[80%] rounded-md p-4 text-sm leading-relaxed whitespace-pre-wrap ${
              m.role === "user" ? "bg-terracotta text-white" : "bg-af-subtle border border-af text-af"
            }`}>
              {m.content}
            </div>
          </div>
        ))}
        {sending && (
          <div className="flex justify-start">
            <div className="bg-af-subtle border border-af rounded-md p-4 text-sm text-af-muted">
              <span className="inline-block animate-pulse">L'assistant rédige…</span>
            </div>
          </div>
        )}
        <div ref={endRef} />
      </div>
      <form onSubmit={send} className="p-4 border-t border-af flex gap-2">
        <Input
          data-testid="chat-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Posez votre question…"
          disabled={sending}
          className="border-af focus-visible:ring-terracotta"
        />
        <Button type="submit" disabled={sending || !input.trim()}
          data-testid="chat-send-btn"
          className="bg-terracotta hover:bg-[#9C7124] text-white rounded-md">
          <Send className="h-4 w-4" />
        </Button>
      </form>
    </Card>
  );
};
